import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import Tag from "./Tag";
import styles from "./TagInput.module.scss";
import { Keyword } from "../../../../dataTypes/keywordManagment/types";

interface TagInputProps {
  availableTags: Keyword[];
  initialTags: Keyword[];
  onTagListChange: (tags: Keyword[]) => void;
  onTagCreate: (keyword: Keyword) => Promise<number>;
}

const TagInput: React.FC<TagInputProps> = ({
  availableTags,
  initialTags,
  onTagListChange,
  onTagCreate,
}) => {
  const [tags, setTags] = useState<Keyword[]>(initialTags);
  const [inputValue, setInputValue] = useState<string>("");
  const [suggestions, setSuggestions] = useState<Keyword[]>([]);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // --- Utils ---

  const normalizedKey = (s: string) =>
    s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const toTitleCaseFr = (raw: string, locale: string = "fr-FR") => {
    const s = raw.trim().normalize("NFC").toLocaleLowerCase(locale);
    if (!s) return s;
    const m = s.match(/\p{L}/u);
    if (!m || m.index === undefined) return s;
    const i = m.index;
    return s.slice(0, i) + s[i].toLocaleUpperCase(locale) + s.slice(i + 1);
  };

  const hasTag = (word: string) =>
    tags.some((t) => normalizedKey(t.word) === normalizedKey(word));

  const hasTagById = (id: number) => tags.some((t) => t.id === id);

  const addTag = (tag: Keyword) => {
    const formattedWord = toTitleCaseFr(tag.word);

    if (hasTagById(tag.id) || hasTag(formattedWord)) return;

    const updated = [...tags, { ...tag, word: formattedWord }];
    setTags(updated);
    onTagListChange(updated);
  };

  // --- Effects ---

  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        textAreaRef.current?.blur();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // --- Handlers ---

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setInputValue(value);

    const needle = normalizedKey(value);

    const filteredSuggestions = availableTags
      .filter((t) => normalizedKey(t.word).includes(needle))
      .filter((t) => !hasTagById(t.id) && !hasTag(t.word));

    setSuggestions(filteredSuggestions);
  };

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter" && inputValue.trim()) {
      event.preventDefault();

      const raw = inputValue.trim();

      const existing =
        availableTags.find(
          (t) => normalizedKey(t.word) === normalizedKey(raw)
        ) ||
        tags.find((t) => normalizedKey(t.word) === normalizedKey(raw));

      if (existing) {
        addTag(existing);
      } else {
        const draft: Keyword = { id: Date.now(), word: toTitleCaseFr(raw) };

        const newId = await onTagCreate(draft);

        if (newId !== 0) {
          addTag({ ...draft, id: newId });
        }
      }

      setInputValue("");
      setSuggestions([]);
    }
  };

  const handleRemoveTag = (index: number) => {
    const updatedTags = tags.filter((_, i) => i !== index);
    setTags(updatedTags);
    onTagListChange(updatedTags);
  };

  const handleSuggestionClick = (suggestion: Keyword) => {
    addTag(suggestion);
    setInputValue("");
    setSuggestions([]);
    textAreaRef.current?.focus();
  };

  // --- Render ---

  return (
    <div ref={containerRef} className={classNames(styles.tagInputContainer)}>
      <div
        className={classNames(styles.tagArea)}
        onClick={() => textAreaRef.current?.focus()}
      >
        {tags.map((tag, index) => (
          <Tag
            key={tag.id}
            keyword={tag}
            onRemove={() => handleRemoveTag(index)}
          />
        ))}
      </div>

      <textarea
        ref={textAreaRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder="Tapez un mot-clé..."
        className={classNames(styles.inputArea)}
      />

      {inputValue && suggestions.length > 0 && (
        <div className={classNames(styles.suggestionsContainer)}>
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              className={classNames(styles.suggestionItem)}
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.word}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagInput;